/**
 * Centralized XBRL Transformation Pattern Constants
 *
 * This file contains all regex patterns and mappings used across the XBRL data
 * transformation service. Centralizing these patterns ensures consistency and
 * makes maintenance easier.
 *
 * @module xbrl-transformation-patterns
 */

/**
 * Regular expression patterns for XBRL key format matching
 */
export const XBRL_PATTERNS = {
  /**
   * Standard bracket format: qname[Consolidated][CurrentYear]
   *
   * Matches keys like:
   * - "ifrs-full:Assets[Consolidated][CurrentYear]"
   * - "ssmt-mfrs:Revenue[Separate][PreviousYear]"
   *
   * Capture groups:
   * 1. qname (e.g., "ifrs-full:Assets")
   * 2. dimension (e.g., "Consolidated" or "Separate")
   * 3. period (e.g., "CurrentYear" or "PreviousYear")
   */
  BRACKET_FORMAT: /^(.+)\[(Consolidated|Separate)\]\[(CurrentYear|PreviousYear)\]$/,

  /**
   * Member bracket format: qname:member[Consolidated][CurrentYear]
   *
   * Matches keys like:
   * - "ifrs-full:Equity:IssuedCapitalMember[Consolidated][CurrentYear]"
   * - "ssmt-mpers:Revenue:ParentMember[Separate][PreviousYear]"
   *
   * Capture groups:
   * 1. qname (e.g., "ifrs-full:Equity")
   * 2. member (e.g., "IssuedCapitalMember")
   * 3. dimension (e.g., "Consolidated" or "Separate")
   * 4. period (e.g., "CurrentYear" or "PreviousYear")
   */
  MEMBER_BRACKET_FORMAT: /^(.+):([^[]+)\[(Consolidated|Separate)\]\[(CurrentYear|PreviousYear)\]$/,

  /**
   * Legacy underscore format (Role 19 old): qname_member_Consolidated_Current
   *
   * Used for migration from old underscore-separated format to bracket format.
   *
   * Matches keys like:
   * - "ifrs-full:ChangesInEquity_ifrs-full:IssuedCapitalMember_Consolidated_Current"
   *
   * Capture groups:
   * 1. qname (e.g., "ifrs-full:ChangesInEquity")
   * 2. member (e.g., "ifrs-full:IssuedCapitalMember")
   * 3. dimension (e.g., "Consolidated" or "Separate")
   * 4. period (e.g., "Current" or "Previous")
   */
  LEGACY_UNDERSCORE: /^(.+)_(.+)_(Consolidated|Separate)_(Current|Previous)$/,

  /**
   * Legacy suffix format (Role 12-18 old): qnameConsolidatedCurrent
   *
   * Used for migration from old concatenated suffix format to bracket format.
   *
   * Matches suffixes:
   * - "ConsolidatedCurrent"
   * - "ConsolidatedPrevious"
   * - "SeparateCurrent"
   * - "SeparatePrevious"
   */
  LEGACY_SUFFIX: /(ConsolidatedCurrent|ConsolidatedPrevious|SeparateCurrent|SeparatePrevious)$/,

  /**
   * Legacy prefixed member (Role 8-9 old): [ifrs-full:ConsolidatedMember]
   *
   * Used for migration from old bracket member format to new simplified format.
   *
   * Old format: qname[ifrs-full:ConsolidatedMember][CurrentYear]
   * New format: qname[Consolidated][CurrentYear]
   *
   * Matches patterns like:
   * - "[ifrs-full:ConsolidatedMember]" → should become "[Consolidated]"
   * - "[ifrs-smes:ConsolidatedMember]" → should become "[Consolidated]"
   * - "[ifrs-full:SeparateMember]" → should become "[Separate]"
   * - "[ifrs-smes:SeparateMember]" → should become "[Separate]"
   *
   * Global flag for replacing all occurrences.
   */
  LEGACY_PREFIXED_MEMBER: /\[(ifrs-full|ifrs-smes):(ConsolidatedMember|SeparateMember)\]/g,

  /**
   * 4-segment QName (Role 19 backend): prefix:concept:period:member
   *
   * Matches backend format for Statement of Changes in Equity:
   * - "ifrs-full:Equity:Beginning:IssuedCapitalMember"
   * - "ifrs-full:Equity:End:EquityAttributableToOwnersOfParentMember"
   *
   * Capture groups:
   * 1. prefix (e.g., "ifrs-full")
   * 2. concept (e.g., "Equity")
   * 3. period (e.g., "Beginning" or "End")
   * 4. member (e.g., "IssuedCapitalMember")
   */
  FOUR_SEGMENT_QNAME: /^([^:]+):([^:]+):(Beginning|End):([^:]+)$/,

  /**
   * Double bracket format (Role 23, 24): qname[dimension][member]
   *
   * Matches keys like:
   * - "ifrs-full:Assets[ifrs-full:ConsolidatedMember][ifrs-full:OrdinarySharesMember]"
   * - "ssmt:Revenue[ifrs-full:SeparateMember][Total]"
   *
   * Capture groups:
   * 1. qname (e.g., "ifrs-full:Assets")
   * 2. dimension (e.g., "ifrs-full:ConsolidatedMember")
   * 3. member (e.g., "ifrs-full:OrdinarySharesMember" or "Total")
   */
  DOUBLE_BRACKET: /^(.+?)\[(.+?)\]\[(.+?)\]$/,

  /**
   * Business member bracket format (Role 2): qname[ssmt:BusinessOneMember]
   *
   * Matches keys like:
   * - "ssmt:MSICCode[ssmt:BusinessOneMember]"
   * - "ssmt:DescriptionOfBusiness[ssmt:BusinessTwoMember]"
   *
   * Capture groups:
   * 1. qname (e.g., "ssmt:MSICCode")
   * 2. member (e.g., "ssmt:BusinessOneMember")
   */
  BUSINESS_MEMBER: /^(.+?)\[(.+?)\]$/,
};

/**
 * Dimension and period name mappings
 */
export const XBRL_DIMENSIONS = {
  /**
   * Dimension name mappings
   * Maps between different representations of dimension names
   */
  dimension: {
    // Standard frontend format
    Consolidated: 'Consolidated',
    Separate: 'Separate',

    // Backend lowercase versions (used in API responses)
    con: 'Consolidated',
    sep: 'Separate',
  } as const,

  /**
   * Period name mappings
   * Maps between different representations of period names
   */
  period: {
    // Standard frontend format
    CurrentYear: 'CurrentYear',
    PreviousYear: 'PreviousYear',

    // Backend versions (various formats)
    Cur: 'CurrentYear',
    Prev: 'PreviousYear',
    Current: 'CurrentYear',
    Previous: 'PreviousYear',
  } as const,

  /**
   * Backend API key mappings
   * Maps dimension+period combinations to backend API keys
   */
  backendKeys: {
    ConsolidatedCurrent: 'conCur',
    ConsolidatedPrevious: 'conPrev',
    SeparateCurrent: 'sepCur',
    SeparatePrevious: 'sepPrev',
  } as const,
};

/**
 * Member name mappings for business segments (Role 2)
 */
export const BUSINESS_MEMBER_MAP = {
  /**
   * Backend to frontend mapping
   * Maps camelCase backend keys to PascalCase frontend keys with prefix
   */
  toFrontend: {
    businessOneMember: 'ssmt:BusinessOneMember',
    businessTwoMember: 'ssmt:BusinessTwoMember',
    businessThreeMember: 'ssmt:BusinessThreeMember',
  } as const,

  /**
   * Frontend to backend mapping
   * Maps PascalCase frontend keys with prefix to camelCase backend keys
   */
  toBackend: {
    'ssmt:BusinessOneMember': 'businessOneMember',
    'ssmt:BusinessTwoMember': 'businessTwoMember',
    'ssmt:BusinessThreeMember': 'businessThreeMember',
  } as const,
};

/**
 * Build backend API key from dimension and period
 *
 * @param dimension - "Consolidated" or "Separate"
 * @param period - "CurrentYear" or "PreviousYear"
 * @returns Backend API key (e.g., "conCur", "sepPrev")
 *
 * @example
 * buildBackendKey('Consolidated', 'CurrentYear')  // Returns: 'conCur'
 * buildBackendKey('Separate', 'PreviousYear')     // Returns: 'sepPrev'
 */
export function buildBackendKey(
  dimension: 'Consolidated' | 'Separate',
  period: 'CurrentYear' | 'PreviousYear'
): string {
  const dim = dimension === 'Consolidated' ? 'con' : 'sep';
  const per = period === 'CurrentYear' ? 'Cur' : 'Prev';
  return `${dim}${per}`;
}

/**
 * Build frontend bracket format key
 *
 * @param qname - QName (e.g., "ifrs-full:Assets")
 * @param dimension - "Consolidated" or "Separate"
 * @param period - "CurrentYear" or "PreviousYear"
 * @param member - Optional member name (e.g., "IssuedCapitalMember")
 * @returns Frontend bracket format key
 *
 * @example
 * buildFrontendKey('ifrs-full:Assets', 'Consolidated', 'CurrentYear')
 * // Returns: 'ifrs-full:Assets[Consolidated][CurrentYear]'
 *
 * buildFrontendKey('ifrs-full:Equity', 'Consolidated', 'CurrentYear', 'IssuedCapitalMember')
 * // Returns: 'ifrs-full:Equity:IssuedCapitalMember[Consolidated][CurrentYear]'
 */
export function buildFrontendKey(
  qname: string,
  dimension: 'Consolidated' | 'Separate',
  period: 'CurrentYear' | 'PreviousYear',
  member?: string
): string {
  const baseKey = member ? `${qname}:${member}` : qname;
  return `${baseKey}[${dimension}][${period}]`;
}

/**
 * Parse backend API key into dimension and period components
 *
 * @param backendKey - Backend API key (e.g., "conCur", "sepPrev")
 * @returns Object with dimension and period, or null if invalid
 *
 * @example
 * parseBackendKey('conCur')
 * // Returns: { dimension: 'Consolidated', period: 'CurrentYear' }
 *
 * parseBackendKey('sepPrev')
 * // Returns: { dimension: 'Separate', period: 'PreviousYear' }
 *
 * parseBackendKey('invalid')
 * // Returns: null
 */
export function parseBackendKey(backendKey: string): {
  dimension: 'Consolidated' | 'Separate';
  period: 'CurrentYear' | 'PreviousYear';
} | null {
  const keyMap: Record<string, { dimension: 'Consolidated' | 'Separate'; period: 'CurrentYear' | 'PreviousYear' }> = {
    conCur: { dimension: 'Consolidated', period: 'CurrentYear' },
    conPrev: { dimension: 'Consolidated', period: 'PreviousYear' },
    sepCur: { dimension: 'Separate', period: 'CurrentYear' },
    sepPrev: { dimension: 'Separate', period: 'PreviousYear' },
    // Uppercase variants (used in Role 19 4-segment QNames)
    ConCur: { dimension: 'Consolidated', period: 'CurrentYear' },
    ConPrev: { dimension: 'Consolidated', period: 'PreviousYear' },
    SepCur: { dimension: 'Separate', period: 'CurrentYear' },
    SepPrev: { dimension: 'Separate', period: 'PreviousYear' },
  };

  return keyMap[backendKey] || null;
}

/**
 * Convert legacy suffix to dimension and period
 *
 * @param suffix - Legacy suffix (e.g., "ConsolidatedCurrent")
 * @returns Object with dimension and period, or null if invalid
 *
 * @example
 * parseLegacySuffix('ConsolidatedCurrent')
 * // Returns: { dimension: 'Consolidated', period: 'CurrentYear' }
 */
export function parseLegacySuffix(suffix: string): {
  dimension: 'Consolidated' | 'Separate';
  period: 'CurrentYear' | 'PreviousYear';
} | null {
  const suffixMap: Record<string, { dimension: 'Consolidated' | 'Separate'; period: 'CurrentYear' | 'PreviousYear' }> = {
    ConsolidatedCurrent: { dimension: 'Consolidated', period: 'CurrentYear' },
    ConsolidatedPrevious: { dimension: 'Consolidated', period: 'PreviousYear' },
    SeparateCurrent: { dimension: 'Separate', period: 'CurrentYear' },
    SeparatePrevious: { dimension: 'Separate', period: 'PreviousYear' },
  };

  return suffixMap[suffix] || null;
}
